/**
 * BIODIVERSITY METRICS UTILITY
 * Calculates ecological diversity indices and conservation metrics
 * Based on standard biodiversity assessment methodologies
 */

import { Tree } from '../types/tree';

// ============================================
// 1. SPECIES DIVERSITY INDICES
// ============================================

export interface DiversityIndices {
  shannonIndex: number;
  simpsonIndex: number;
  simpsonDiversity: number;
  evenness: number;
  speciesRichness: number;
  totalIndividuals: number;
  dominantSpecies: string;
  dominantSpeciesProportion: number;
  interpretation: {
    shannon: string;
    simpson: string;
    evenness: string;
  };
}

/**
 * Calculate Shannon Diversity Index (H')
 * H' = -Σ(pi * ln(pi))
 * where pi = proportion of species i in the community
 *
 * Interpretation:
 * - H' < 1.5: Low diversity
 * - 1.5 ≤ H' < 3.5: Medium diversity
 * - H' ≥ 3.5: High diversity
 */
export function calculateShannonIndex(trees: Tree[]): number {
  const speciesCounts = getSpeciesCounts(trees);
  const totalTrees = trees.length;

  let shannon = 0;
  for (const count of Object.values(speciesCounts)) {
    const proportion = count / totalTrees;
    if (proportion > 0) {
      shannon -= proportion * Math.log(proportion);
    }
  }

  return shannon;
}

/**
 * Calculate Simpson's Diversity Index (D)
 * D = 1 - Σ(pi²)
 * where pi = proportion of species i
 *
 * Also calculates Simpson's Index of Diversity (1/λ)
 * where λ = Σ(pi²)
 *
 * Interpretation:
 * - D close to 0: Low diversity (dominated by few species)
 * - D close to 1: High diversity (even distribution)
 */
export function calculateSimpsonIndex(trees: Tree[]): {
  simpson: number;
  lambda: number;
  diversity: number;
} {
  const speciesCounts = getSpeciesCounts(trees);
  const totalTrees = trees.length;

  let lambda = 0;
  for (const count of Object.values(speciesCounts)) {
    const proportion = count / totalTrees;
    lambda += proportion * proportion;
  }

  const simpson = 1 - lambda;
  const diversity = 1 / lambda; // Simpson's Index of Diversity

  return { simpson, lambda, diversity };
}

/**
 * Calculate Pielou's Evenness Index (J')
 * J' = H' / ln(S)
 * where H' = Shannon index, S = species richness
 *
 * Interpretation:
 * - J' = 1: Perfect evenness (all species equally abundant)
 * - J' < 0.5: Uneven distribution (dominance by few species)
 * - 0.5 ≤ J' < 0.75: Moderate evenness
 * - J' ≥ 0.75: High evenness
 */
export function calculateEvenness(shannon: number, speciesRichness: number): number {
  if (speciesRichness <= 1) return 0;
  return shannon / Math.log(speciesRichness);
}

/**
 * Calculate comprehensive diversity indices
 */
export function calculateDiversityIndices(trees: Tree[]): DiversityIndices {
  const speciesCounts = getSpeciesCounts(trees);
  const speciesRichness = Object.keys(speciesCounts).length;
  const totalIndividuals = trees.length;

  const shannonIndex = calculateShannonIndex(trees);
  const { simpson, diversity: simpsonDiversity } = calculateSimpsonIndex(trees);
  const evenness = calculateEvenness(shannonIndex, speciesRichness);

  // Find dominant species
  let dominantSpecies = '';
  let maxCount = 0;
  for (const [species, count] of Object.entries(speciesCounts)) {
    if (count > maxCount) {
      maxCount = count;
      dominantSpecies = species;
    }
  }
  const dominantSpeciesProportion = maxCount / totalIndividuals;

  // Interpretations
  const shannonInterpretation =
    shannonIndex < 1.5
      ? 'Low diversity - Community dominated by few species'
      : shannonIndex < 3.5
      ? 'Medium diversity - Balanced species distribution'
      : 'High diversity - Rich and varied species composition';

  const simpsonInterpretation =
    simpson < 0.5
      ? 'Low diversity - High dominance by few species'
      : simpson < 0.8
      ? 'Moderate diversity - Some species dominance'
      : 'High diversity - Even species distribution';

  const evennessInterpretation =
    evenness < 0.5
      ? 'Low evenness - Unequal species abundance'
      : evenness < 0.75
      ? 'Moderate evenness - Relatively balanced distribution'
      : 'High evenness - Species are equally abundant';

  return {
    shannonIndex,
    simpsonIndex: simpson,
    simpsonDiversity,
    evenness,
    speciesRichness,
    totalIndividuals,
    dominantSpecies,
    dominantSpeciesProportion,
    interpretation: {
      shannon: shannonInterpretation,
      simpson: simpsonInterpretation,
      evenness: evennessInterpretation,
    },
  };
}

// ============================================
// 2. CONSERVATION METRICS
// ============================================

export interface ConservationMetrics {
  nativeCount: number;
  nativeProportion: number;
  endemicCount: number;
  endemicProportion: number;
  threatenedCount: number;
  threatenedProportion: number;
  invasiveCount: number;
  invasiveProportion: number;
  conservationPriority: string;
  nativeSpeciesRichness: number;
  endemicSpeciesRichness: number;
  threatenedSpeciesRichness: number;
}

/**
 * Calculate conservation status metrics
 */
export function calculateConservationMetrics(trees: Tree[]): ConservationMetrics {
  const totalTrees = trees.length;

  const nativeTrees = trees.filter(t => Number(t.Native) === 1);
  const endemicTrees = trees.filter(t => Number(t.Endemism) === 1);
  // Only count trees with IUCN threatened categories: VU (Vulnerable), EN (Endangered), CR (Critically Endangered), NT (Near Threatened)
  const threatenedTrees = trees.filter(t => {
    const status = String(t.Threat_status || '').trim().toUpperCase();
    return status === 'VU' || status === 'EN' || status === 'CR' || status === 'NT';
  });
  const invasiveTrees = trees.filter(t => Number(t.Invasive) === 1);

  // Species-level counts
  const nativeSpeciesSet = new Set(nativeTrees.map(t => t.Species));
  const endemicSpeciesSet = new Set(endemicTrees.map(t => t.Species));
  const threatenedSpeciesSet = new Set(threatenedTrees.map(t => t.Species));

  // Proportions
  const nativeProportion = nativeTrees.length / totalTrees;
  const endemicProportion = endemicTrees.length / totalTrees;
  const threatenedProportion = threatenedTrees.length / totalTrees;
  const invasiveProportion = invasiveTrees.length / totalTrees;

  // Determine conservation priority
  let conservationPriority = 'Moderate';
  if (threatenedProportion > 0.2 || invasiveProportion > 0.1) {
    conservationPriority = 'High - Immediate action needed';
  } else if (endemicProportion > 0.1 || threatenedProportion > 0.05) {
    conservationPriority = 'Medium - Active monitoring required';
  } else if (nativeProportion > 0.8) {
    conservationPriority = 'Excellent - High native diversity';
  }

  return {
    nativeCount: nativeTrees.length,
    nativeProportion,
    endemicCount: endemicTrees.length,
    endemicProportion,
    threatenedCount: threatenedTrees.length,
    threatenedProportion,
    invasiveCount: invasiveTrees.length,
    invasiveProportion,
    conservationPriority,
    nativeSpeciesRichness: nativeSpeciesSet.size,
    endemicSpeciesRichness: endemicSpeciesSet.size,
    threatenedSpeciesRichness: threatenedSpeciesSet.size,
  };
}

// ============================================
// 3. STRUCTURAL DIVERSITY
// ============================================

export interface StructuralDiversity {
  heightDiversity: {
    mean: number;
    median: number;
    min: number;
    max: number;
    stdDev: number;
    coefficient: number;
  };
  gbhDiversity: {
    mean: number;
    median: number;
    min: number;
    max: number;
    stdDev: number;
    coefficient: number;
  };
  sizeClasses: {
    small: number;
    medium: number;
    large: number;
    extraLarge: number;
  };
  ageStructure: {
    young: number;
    mature: number;
    old: number;
  };
  structuralComplexity: string;
}

/**
 * Calculate structural diversity metrics
 */
export function calculateStructuralDiversity(trees: Tree[]): StructuralDiversity {
  const heights = trees.filter(t => t.Height).map(t => Number(t.Height));
  const gbhs = trees.filter(t => t.GBH).map(t => Number(t.GBH));

  const heightStats = calculateStatistics(heights);
  const gbhStats = calculateStatistics(gbhs);

  // Size classes based on GBH
  const sizeClasses = {
    small: trees.filter(t => t.GBH && Number(t.GBH) < 30).length,
    medium: trees.filter(t => t.GBH && Number(t.GBH) >= 30 && Number(t.GBH) < 94).length,
    large: trees.filter(t => t.GBH && Number(t.GBH) >= 94 && Number(t.GBH) < 239).length,
    extraLarge: trees.filter(t => t.GBH && Number(t.GBH) >= 239).length,
  };

  // Age structure (approximated from GBH)
  const ageStructure = {
    young: sizeClasses.small,
    mature: sizeClasses.medium + sizeClasses.large,
    old: sizeClasses.extraLarge,
  };

  // Assess structural complexity
  const cv = heightStats.coefficient;
  let structuralComplexity = 'Moderate';
  if (cv < 0.2) {
    structuralComplexity = 'Low - Uniform structure';
  } else if (cv < 0.4) {
    structuralComplexity = 'Moderate - Some variation';
  } else {
    structuralComplexity = 'High - Complex vertical structure';
  }

  return {
    heightDiversity: heightStats,
    gbhDiversity: gbhStats,
    sizeClasses,
    ageStructure,
    structuralComplexity,
  };
}

// ============================================
// 4. FUNCTIONAL DIVERSITY
// ============================================

export interface FunctionalDiversity {
  canopyTypes: {
    [key: string]: number;
  };
}

/**
 * Calculate functional diversity metrics
 */
export function calculateFunctionalDiversity(trees: Tree[]): FunctionalDiversity {
  // Canopy types
  const canopyTypes: { [key: string]: number } = {};
  trees.forEach(tree => {
    if (tree.Canopy) {
      canopyTypes[tree.Canopy] = (canopyTypes[tree.Canopy] || 0) + 1;
    }
  });

  return {
    canopyTypes,
  };
}

// ============================================
// 5. COMPREHENSIVE BIODIVERSITY ASSESSMENT
// ============================================

export interface BiodiversityAssessment {
  diversity: DiversityIndices;
  conservation: ConservationMetrics;
  structural: StructuralDiversity;
  functional: FunctionalDiversity;
  overallScore: number;
  rating: string;
  recommendations: string[];
}

/**
 * Calculate comprehensive biodiversity assessment
 */
export function calculateBiodiversityAssessment(trees: Tree[]): BiodiversityAssessment {
  const diversity = calculateDiversityIndices(trees);
  const conservation = calculateConservationMetrics(trees);
  const structural = calculateStructuralDiversity(trees);
  const functional = calculateFunctionalDiversity(trees);

  // Calculate overall biodiversity score (0-100)
  let score = 0;

  // Species diversity (30 points)
  score += (diversity.shannonIndex / 4) * 15; // Shannon (max 15)
  score += diversity.simpsonIndex * 15; // Simpson (max 15)

  // Conservation value (25 points)
  score += conservation.nativeProportion * 10; // Native (max 10)
  score += conservation.endemicProportion * 100; // Endemic (max 10 if 10%+)
  score += Math.min(conservation.threatenedSpeciesRichness * 5, 5); // Threatened presence

  // Structural diversity (20 points)
  const sizeBalance = 1 - Math.abs(
    (structural.sizeClasses.small + structural.sizeClasses.extraLarge) / trees.length - 0.3
  );
  score += sizeBalance * 20;

  // Functional diversity (25 points) - based on canopy type diversity
  const canopyTypes = Object.keys(functional.canopyTypes).length;
  score += Math.min(canopyTypes * 5, 25); // 5 points per canopy type, max 25

  // Cap at 100
  score = Math.min(score, 100);

  // Rating
  let rating = 'Fair';
  if (score >= 80) rating = 'Excellent - Highly biodiverse ecosystem';
  else if (score >= 65) rating = 'Good - Healthy biodiversity';
  else if (score >= 50) rating = 'Fair - Moderate biodiversity';
  else rating = 'Poor - Biodiversity enhancement needed';

  // Generate recommendations
  const recommendations: string[] = [];

  if (diversity.shannonIndex < 2) {
    recommendations.push('Increase species richness by planting diverse native species');
  }
  if (conservation.nativeProportion < 0.6) {
    recommendations.push('Prioritize native species in future plantings');
  }
  if (conservation.invasiveCount > 0) {
    recommendations.push(`Remove or manage ${conservation.invasiveCount} invasive species`);
  }
  if (structural.ageStructure.young < structural.ageStructure.old) {
    recommendations.push('Plant younger trees to ensure succession and future canopy');
  }
  if (conservation.threatenedCount > 0) {
    recommendations.push(`Implement conservation measures for ${conservation.threatenedCount} threatened individuals`);
  }

  if (recommendations.length === 0) {
    recommendations.push('Maintain current biodiversity through regular monitoring');
    recommendations.push('Continue diverse species plantings in replacement programs');
  }

  return {
    diversity,
    conservation,
    structural,
    functional,
    overallScore: score,
    rating,
    recommendations,
  };
}

// ============================================
// 6. UTILITY FUNCTIONS
// ============================================

/**
 * Get species counts
 */
function getSpeciesCounts(trees: Tree[]): { [species: string]: number } {
  const counts: { [species: string]: number } = {};
  trees.forEach(tree => {
    const species = tree.Species || 'Unknown';
    counts[species] = (counts[species] || 0) + 1;
  });
  return counts;
}

/**
 * Calculate statistical measures
 */
function calculateStatistics(values: number[]): {
  mean: number;
  median: number;
  min: number;
  max: number;
  stdDev: number;
  coefficient: number;
} {
  if (values.length === 0) {
    return { mean: 0, median: 0, min: 0, max: 0, stdDev: 0, coefficient: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const median = sorted[Math.floor(sorted.length / 2)];
  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  // Standard deviation
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / values.length;
  const stdDev = Math.sqrt(variance);

  // Coefficient of variation
  const coefficient = mean > 0 ? stdDev / mean : 0;

  return { mean, median, min, max, stdDev, coefficient };
}

/**
 * Get species by conservation status
 */
export function getSpeciesByStatus(trees: Tree[]): {
  native: string[];
  endemic: string[];
  threatened: string[];
  invasive: string[];
} {
  const native = Array.from(new Set(trees.filter(t => Number(t.Native) === 1).map(t => t.Species)));
  const endemic = Array.from(new Set(trees.filter(t => Number(t.Endemism) === 1).map(t => t.Species)));
  // Only count species with IUCN threatened categories: VU, EN, CR, NT
  const threatened = Array.from(new Set(trees.filter(t => {
    const status = String(t.Threat_status || '').trim().toUpperCase();
    return status === 'VU' || status === 'EN' || status === 'CR' || status === 'NT';
  }).map(t => t.Species)));
  const invasive = Array.from(new Set(trees.filter(t => Number(t.Invasive) === 1).map(t => t.Species)));

  return { native, endemic, threatened, invasive };
}

/**
 * Calculate species accumulation curve (for rarefaction analysis)
 */
export function calculateSpeciesAccumulation(trees: Tree[], steps: number = 10): {
  individuals: number[];
  species: number[];
} {
  const individuals: number[] = [];
  const species: number[] = [];
  const speciesSet = new Set<string>();

  const stepSize = Math.floor(trees.length / steps);

  for (let i = stepSize; i <= trees.length; i += stepSize) {
    individuals.push(i);
    for (let j = 0; j < i; j++) {
      speciesSet.add(trees[j].Species);
    }
    species.push(speciesSet.size);
  }

  return { individuals, species };
}

/**
 * Get top N species by abundance
 */
export function getTopSpecies(trees: Tree[], n: number = 10): Array<{
  species: string;
  commonName: string;
  count: number;
  proportion: number;
}> {
  const speciesCounts = getSpeciesCounts(trees);
  const total = trees.length;

  const speciesData = Object.entries(speciesCounts)
    .map(([species, count]) => {
      const tree = trees.find(t => t.Species === species);
      return {
        species,
        commonName: tree?.Common_Name || species,
        count,
        proportion: count / total,
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, n);

  return speciesData;
}
